import ReactMarkdown from 'react-markdown';
import { Link as RouterLink } from 'react-router-dom';
import '../../utils/highlight';
// markdown plugins
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
// @mui
import { Divider, Link, Typography } from '@mui/material';
//
import Image from '../image';
//
import StyledMarkdown from './styles';
import { MarkdownProps } from './types';
import { delatexify } from './delatexify';

// ----------------------------------------------------------------------

export default function Markdown({ sx, className, children, ...other }: MarkdownProps) {
  const content = typeof children === 'string' ? delatexify(children) : children;
  return (
    // Forward `className` to the styled root, NOT to <ReactMarkdown>. emotion's
    // `styled(Markdown)(...)` overrides arrive here as `className`; if spread onto
    // <ReactMarkdown> it only lands on react-markdown v8's extra wrapper <div>
    // (and v9 drops `className` entirely), so the override never sits on the styled
    // root. Putting it on StyledMarkdown keeps those overrides working without
    // depending on the wrapper div. `sx` overrides land here too.
    <StyledMarkdown sx={sx} className={className}>
      <ReactMarkdown
        rehypePlugins={[
          rehypeRaw,
          rehypeHighlight,
          [remarkGfm, { singleTilde: false }],
        ]}
        components={components}
        {...other}
      >
        {content}
      </ReactMarkdown>
    </StyledMarkdown>
  );
}

// ----------------------------------------------------------------------

const components = {
  h1: ({ ...props }) => (
    <Typography component="span" variant="h1" gutterBottom {...props} />
  ),
  h2: ({ ...props }) => (
    <Typography component="span" variant="h2" gutterBottom {...props} />
  ),
  h3: ({ ...props }) => (
    <Typography component="span" variant="h3" gutterBottom {...props} />
  ),
  h4: ({ ...props }) => (
    <Typography component="span" variant="h4" gutterBottom {...props} />
  ),
  h5: ({ ...props }) => (
    <Typography component="span" variant="h5" gutterBottom {...props} />
  ),
  h6: ({ ...props }) => (
    <Typography component="span" variant="h6" gutterBottom {...props} />
  ),
  p: ({ ...props }) => {
    // Check if paragraph only contains a br (empty line)
    const children = props.children;
    const isEmpty =
      !children ||
      (Array.isArray(children) && children.length === 0) ||
      (typeof children === 'object' && children?.type === 'br');
    if (isEmpty) {
      return <Typography component="p" sx={{ minHeight: '1em', mb: 1 }} />;
    }
    return <Typography component="p" sx={{ mb: 1 }} {...props} />;
  },
  br: () => <br />,
  hr: ({ ...props }) => <Divider sx={{ my: 3 }} {...props} />,
  img: ({ ...props }) => (
    <Image
      alt={props.alt}
      ratio="16/9"
      sx={{ borderRadius: 2, my: 5 }}
      {...props}
    />
  ),
  a: ({ ...props }) => {
    const isHttp = props.href.includes('http');

    return isHttp ? (
      <Link target="_blank" rel="noopener" {...props} />
    ) : (
      <Link component={RouterLink} to={props.href} {...props}>
        {props.children}
      </Link>
    );
  },
};
